import { Select } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { toast } from "@msuite/ui/toast";
import { getPartnerReportStateColor } from "@msuite/utils/partnerships";
import { partnershipReportReferenceFormSchema } from "@msuite/utils/schemas";
import type { Enums, PartnerTimelineInvoiceTrackingItem } from "@msuite/utils/types";
import { useForm } from "react-hook-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

export function RowActions({
	cell,
}: CellContext<Required<PartnerTimelineInvoiceTrackingItem>, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Form */
	const methods = useForm({
		defaultValues: {
			state: cell.row.original.state,
		},
		resolver: resolver(partnershipReportReferenceFormSchema),
	});

	/** Function */
	async function handleOnChange(value: Enums<"partnerReportState">) {
		try {
			await client.organizations[":orgId"].partnerships[":partnershipId"].timeline.report.reference[
				":referenceId"
			].$patch({
				param: {
					orgId: organization!.id!,
					partnershipId: cell.row.original.partnershipId,
					referenceId: cell.row.original.id,
				},
				json: {
					state: value,
				},
			});
			await refetch?.();
		} catch (error) {
			console.error(error);
			toast.error(t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<RowActionsWrapper to={null}>
			<div className="w-full">
				<Select
					className="py-1 rounded-sm"
					label={undefined}
					onChange={(value) => {
						handleOnChange(value as Enums<"partnerReportState">);
					}}
					isRequired={false}
					isNullable={false}
					renderOption={(item) => (
						<Tag
							variant="plain"
							color={getPartnerReportStateColor(item.state)}
						>
							{t(`general.${item.state}`)}
						</Tag>
					)}
					options={
						[
							{
								label: t("general.pending"),
								value: "pending",
								item: {
									state: "pending",
								},
							},
							{
								label: t("general.approved"),
								value: "approved",
								item: {
									state: "approved",
								},
							},
						] as const
					}
					control={methods.control}
					name="state"
					itemNotFoundLabel="Not found"
				/>
			</div>
		</RowActionsWrapper>
	);
}
